import React, { useState, useCallback } from 'react';
import { Box, Typography, List, ListItem, ListItemText, Chip } from '@mui/material';
import { useGameEvents } from '../hooks/useGameEvents';

export interface ActivityEntry {
  id: string;
  actor: string;
  action: string;
  target: string;
  timestamp: string;
}

// ActivityFeed receives entries via game events. It maintains a local rolling list.
const MAX_ENTRIES = 20;

const ActivityFeed: React.FC = () => {
  const [entries, setEntries] = useState<ActivityEntry[]>([]);

  const handleActivity = useCallback((entry: ActivityEntry) => {
    setEntries((prev) => [entry, ...prev].slice(0, MAX_ENTRIES));
  }, []);

  // Register a listener for activity events emitted by game events hook
  useGameEvents(handleActivity);

  if (entries.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No recent activity.
        </Typography>
      </Box>
    );
  }

  return (
    <List disablePadding dense>
      {entries.map((entry) => (
        <ListItem key={entry.id} disablePadding sx={{ py: 0.25 }}>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip label={entry.actor} size="small" variant="outlined" sx={{ height: 18 }} />
                <Typography variant="caption">{entry.action}</Typography>
                <Typography variant="caption" fontWeight="medium">{entry.target}</Typography>
              </Box>
            }
            secondary={new Date(entry.timestamp).toLocaleTimeString()}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
        </ListItem>
      ))}
    </List>
  );
};

export default ActivityFeed;
